import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConversationProvider, useConversation } from '@elevenlabs/react';
import { useAuth } from '../hooks/useAuth';
import { useVoiceRecognition } from '../hooks/useVoiceRecognition';
import { useTranslation } from '../hooks/useTranslation';
import { api } from '../lib/api-client';
import { Button } from './ui/Button';
import { Spinner } from './ui/Spinner';

const AGENT_ID = 'agent_8501kpzga61hfett4b5v1evzw1xk';

function MicrophoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Field label keys for the Pro review form (resolved via t() at render time)
// ---------------------------------------------------------------------------
const FIELD_LABEL_KEYS: Record<string, string> = {
  aeroportoPartenza: 'voice.fieldDeparture',
  aeroportoArrivo: 'voice.fieldDestination',
  tipoMonitoraggio: 'voice.fieldType',
  dataAndata: 'voice.fieldDepartureDate',
  dataRitorno: 'voice.fieldReturnDate',
  soloAndata: 'voice.fieldOneWay',
  periodoInizio: 'voice.fieldPeriodFrom',
  periodoFine: 'voice.fieldPeriodTo',
  budgetMax: 'voice.fieldBudget',
  passeggeri: 'voice.fieldPassengers',
  classeCabina: 'voice.fieldClass',
  soggiornoMin: 'voice.fieldStayMin',
  soggiornoMax: 'voice.fieldStayMax',
};

function makeFormatValue(t: (k: string) => string) {
  return function formatValue(key: string, value: unknown): string {
    if (value == null || value === '') return '';
    switch (key) {
      case 'budgetMax': return `€${value}`;
      case 'soloAndata': return value ? t('voice.yes') : t('voice.no');
      case 'classeCabina': {
        const m: Record<string, string> = {
          economy: t('voice.classEconomy'),
          business: t('voice.classBusiness'),
          first: t('voice.classFirst'),
          premium_economy: t('voice.classPremiumEconomy'),
        };
        return m[value as string] || String(value);
      }
      case 'tipoMonitoraggio': return value === 'flessibile' ? t('voice.typeFlexible') : t('voice.typeFixed');
      case 'soggiornoMin': case 'soggiornoMax': return `${value} ${t('voice.days')}`;
      default: return String(value);
    }
  };
}

// ---------------------------------------------------------------------------
// Modal wrapper
// ---------------------------------------------------------------------------
interface Props {
  open: boolean;
  onClose: () => void;
}

export function VoiceSearchModal(props: Props) {
  return (
    <ConversationProvider>
      <VoiceSearchModalInner {...props} />
    </ConversationProvider>
  );
}

// ---------------------------------------------------------------------------
// Inner modal — routes to Pro or Unlimited experience
// ---------------------------------------------------------------------------
interface VoiceSearchResponse {
  fields: Record<string, unknown>;
  interpretation: string;
  missing: string[];
}

function VoiceSearchModalInner({ open, onClose }: Props) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const isPro = user?.plan === 'premium';
  const isUnlimited = user?.plan === 'unlimited';
  const [unlimitedMode, setUnlimitedMode] = useState<'choose' | 'compile' | 'agent'>('choose');

  // Reset mode when modal reopens
  useEffect(() => {
    if (open) setUnlimitedMode('choose');
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-white flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-background rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto p-6 relative">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-text-secondary hover:text-text-primary transition-colors cursor-pointer"
          aria-label={t('common.close')}
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {isPro && <ProMode onClose={onClose} />}

        {isUnlimited && unlimitedMode === 'choose' && (
          <div className="flex flex-col items-center text-center pt-4">
            <div className="mb-6 w-20 h-20 rounded-full bg-primary-50 flex items-center justify-center">
              <MicrophoneIcon className="h-10 w-10 text-primary-500" />
            </div>
            <h2 className="text-xl font-semibold text-text-primary mb-2">{t('voice.howProceed')}</h2>
            <p className="text-sm text-text-secondary mb-6">{t('voice.chooseMode')}</p>
            <div className="flex flex-col gap-3 w-full">
              <button
                onClick={() => setUnlimitedMode('compile')}
                className="w-full p-4 rounded-xl border border-border bg-surface hover:border-primary-400 transition-colors text-left cursor-pointer"
              >
                <div className="font-medium text-text-primary mb-1">{t('voice.aiCompile')}</div>
                <div className="text-xs text-text-secondary">{t('voice.aiCompileDesc')}</div>
              </button>
              <button
                onClick={() => setUnlimitedMode('agent')}
                className="w-full p-4 rounded-xl border-2 border-primary-500 bg-primary-50 hover:bg-primary-100 transition-colors text-left cursor-pointer"
              >
                <div className="font-medium text-primary-700 mb-1">{t('voice.aiAgent')}</div>
                <div className="text-xs text-primary-600">{t('voice.aiAgentDesc')}</div>
              </button>
            </div>
          </div>
        )}
        {isUnlimited && unlimitedMode === 'compile' && <ProMode onClose={onClose} />}
        {isUnlimited && unlimitedMode === 'agent' && <UnlimitedMode onClose={onClose} />}

        {!isPro && !isUnlimited && <FreeGate onClose={onClose} />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FREE — upgrade gate
// ---------------------------------------------------------------------------
function FreeGate({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center text-center pt-4">
      <div className="mb-4 w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center">
        <svg className="h-8 w-8 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
        </svg>
      </div>
      <h2 className="text-xl font-semibold text-text-primary mb-2">{t('voice.proFunction')}</h2>
      <p className="text-sm text-text-secondary mb-6">
        {t('voice.proFunctionDesc')}
      </p>
      <div className="flex flex-col gap-2 w-full">
        <Button size="lg" onClick={() => { onClose(); navigate('/subscription'); }} className="w-full">
          {t('voice.discoverPlans')}
        </Button>
        <Button variant="outline" size="lg" onClick={onClose} className="w-full">
          {t('voice.close')}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PRO — one-shot voice → Claude Haiku → form review → confirm
// ---------------------------------------------------------------------------
type ProState = 'idle' | 'listening' | 'processing' | 'review' | 'creating' | 'done';

function ProMode({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const tStr = t as (k: string) => string;
  const formatValue = makeFormatValue(tStr);
  const {
    isSupported, isListening, transcript,
    startListening, stopListening, resetTranscript,
  } = useVoiceRecognition();

  const [state, setState] = useState<ProState>('idle');
  const [fields, setFields] = useState<Record<string, unknown>>({});
  const [interpretation, setInterpretation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [textInput, setTextInput] = useState('');
  const [useText, setUseText] = useState(false);
  const prevListening = useRef(false);

  // Auto-transition: when speech ends, auto-process transcript
  useEffect(() => {
    if (prevListening.current && !isListening && state === 'listening') {
      const text = transcript.trim().slice(0, 10000);
      if (text) {
        setState('processing');
        extract(text);
      } else {
        setError(t('voice.didntHearAnything'));
        setState('idle');
      }
    }
    prevListening.current = isListening;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isListening, transcript, state]);

  // Web Speech API not supported on iOS Safari — auto-fallback to text
  const canUseVoice = isSupported && !/iPhone|iPad|iPod/.test(navigator.userAgent);

  function handleStartListening() {
    setError(null);
    startListening();
    setState('listening');
  }

  async function extract(text: string) {
    setError(null);
    try {
      const data = await api.post<VoiceSearchResponse>('/ai/voice-search', { transcript: text });
      setFields(data.fields);
      setInterpretation(data.interpretation);

      if (!data.fields.aeroportoPartenza || !data.fields.aeroportoArrivo) {
        setError(t('voice.didntHearAirports'));
        setState('idle');
        return;
      }
      const hasDate = data.fields.dataAndata || (data.fields.periodoInizio && data.fields.periodoFine);
      if (!hasDate) {
        setError(t('voice.didntHearDates'));
        setState('idle');
        return;
      }
      setState('review');
    } catch (err: any) {
      setError(err.message || t('voice.errorAnalysis'));
      setState('idle');
    }
  }

  async function createMonitor() {
    setState('creating');
    setError(null);
    try {
      const f = fields;
      const body: Record<string, unknown> = {
        aeroportoPartenza: f.aeroportoPartenza,
        aeroportoArrivo: f.aeroportoArrivo,
        tipoMonitoraggio: f.tipoMonitoraggio || 'fisso',
        budgetMax: f.budgetMax ?? null,
        classeCabina: f.classeCabina ?? null,
        passeggeri: f.passeggeri ?? 1,
        soggiornoMin: f.soggiornoMin ?? null,
        soggiornoMax: f.soggiornoMax ?? null,
      };
      if ((f.tipoMonitoraggio || 'fisso') === 'fisso') {
        body.dataAndata = f.dataAndata;
        body.dataRitorno = f.soloAndata ? null : (f.dataRitorno || null);
        body.soloAndata = f.soloAndata ?? !f.dataRitorno;
      } else {
        body.periodoInizio = f.periodoInizio;
        body.periodoFine = f.periodoFine;
        body.dataAndata = f.periodoInizio;
      }
      await api.post('/monitoraggi', body);
      setState('done');
    } catch (err: any) {
      setError(err.message || t('voice.errorCreation'));
      setState('review');
    }
  }

  function handleRetry() {
    resetTranscript();
    setFields({});
    setInterpretation('');
    setError(null);
    setTextInput('');
    setUseText(false);
    setState('idle');
  }

  return (
    <div className="flex flex-col items-center text-center pt-4">
      {/* IDLE — voice primary, text fallback */}
      {state === 'idle' && (
        <>
          <div className="mb-5 w-20 h-20 rounded-full bg-primary-50 flex items-center justify-center">
            <MicrophoneIcon className="h-10 w-10 text-primary-500" />
          </div>
          <h2 className="text-xl font-semibold text-text-primary mb-2">{t('voice.aiCompile')}</h2>
          <p className="text-sm text-text-secondary mb-4">
            {t('voice.describeFlight')}
          </p>

          {error && (
            <div className="w-full mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>
          )}

          {!useText && canUseVoice ? (
            <div className="w-full space-y-3">
              <Button size="lg" onClick={handleStartListening} className="w-full">
                {t('voice.speakNow')}
              </Button>
              <button
                onClick={() => setUseText(true)}
                className="text-sm text-text-secondary hover:text-primary-600 transition-colors cursor-pointer"
              >
                {t('voice.preferWriting')}
              </button>
            </div>
          ) : (
            <div className="w-full space-y-3">
              <textarea
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                placeholder={t('voice.inputPlaceholder')}
                rows={3}
                className="w-full px-4 py-3 rounded-lg border border-border bg-surface text-text-primary placeholder:text-text-secondary text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              />
              <Button size="lg" onClick={() => { setState('processing'); extract(textInput.trim()); }} disabled={!textInput.trim()} className="w-full">
                {t('voice.compile')}
              </Button>
              {canUseVoice && (
                <button
                  onClick={() => setUseText(false)}
                  className="text-sm text-text-secondary hover:text-primary-600 transition-colors cursor-pointer"
                >
                  {t('voice.preferSpeaking')}
                </button>
              )}
            </div>
          )}
        </>
      )}

      {/* LISTENING */}
      {state === 'listening' && (
        <>
          <p className="text-sm font-medium text-text-secondary mb-6">{t('voice.listening')}</p>
          <div className="relative mb-8">
            <div className="absolute inset-0 rounded-full bg-primary-400/30 animate-ping" />
            <div className="relative w-20 h-20 rounded-full bg-primary-500 flex items-center justify-center">
              <MicrophoneIcon className="h-10 w-10 text-white" />
            </div>
          </div>
          {transcript && (
            <p className="text-text-primary text-base mb-6 min-h-[2rem]">{transcript}</p>
          )}
          <p className="text-xs text-text-secondary mb-4">{t('voice.autoStops')}</p>
          <Button variant="outline" size="lg" onClick={() => { stopListening(); }} className="w-full">{t('voice.interrupt')}</Button>
        </>
      )}

      {/* PROCESSING */}
      {state === 'processing' && (
        <div className="py-12">
          <Spinner size="lg" />
          <p className="text-text-secondary mt-4">{t('voice.analyzing')}</p>
        </div>
      )}

      {/* REVIEW — form pre-compilato, utente verifica e conferma */}
      {state === 'review' && (
        <>
          <h2 className="text-lg font-semibold text-text-primary mb-1">{t('voice.verifyDetails')}</h2>
          <p className="text-sm italic text-text-secondary mb-4">{interpretation}</p>

          <div className="w-full rounded-xl border border-border bg-surface p-4 space-y-2.5 mb-4 text-left">
            {Object.entries(fields).map(([key, value]) => {
              if (value == null || value === '' || !FIELD_LABEL_KEYS[key]) return null;
              return (
                <div key={key} className="flex justify-between text-sm">
                  <span className="text-text-secondary">{tStr(FIELD_LABEL_KEYS[key])}</span>
                  <span className="font-medium text-text-primary">{formatValue(key, value)}</span>
                </div>
              );
            })}
          </div>

          {error && (
            <div className="w-full mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>
          )}

          <div className="flex flex-col gap-2 w-full">
            <Button size="lg" onClick={createMonitor} className="w-full">
              {t('voice.create')}
            </Button>
            <Button variant="outline" size="lg" onClick={handleRetry} className="w-full">
              {t('voice.retry')}
            </Button>
          </div>
        </>
      )}

      {/* CREATING */}
      {state === 'creating' && (
        <div className="py-12">
          <Spinner size="lg" />
          <p className="text-text-secondary mt-4">{t('voice.creating')}</p>
        </div>
      )}

      {/* DONE */}
      {state === 'done' && (
        <>
          <div className="mb-4 w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
            <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-text-primary mb-2">{t('voice.created')}</h2>
          <p className="text-sm text-text-secondary mb-6">{interpretation}</p>
          <div className="flex flex-col gap-2 w-full">
            <Button size="lg" onClick={() => { onClose(); navigate('/monitors'); }} className="w-full">
              {t('voice.goToMonitors')}
            </Button>
            <Button variant="outline" size="lg" onClick={handleRetry} className="w-full">
              {t('voice.newSearch')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// UNLIMITED — ElevenLabs conversational agent
// ---------------------------------------------------------------------------
function UnlimitedMode(_props: { onClose: () => void }) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  const conversation = useConversation({
    onError: (err: string) => {
      console.error('[ElevenLabs] Error:', err);
      setError(t('voice.connectionError'));
    },
    onDisconnect: () => {},
  });

  const { status, isSpeaking } = conversation;
  const isConnected = status === 'connected';
  const isConnecting = status === 'connecting';

  const handleStart = useCallback(async () => {
    setError(null);
    try {
      if (!user?.id) {
        setError(t('voice.mustAuth'));
        return;
      }
      await navigator.mediaDevices.getUserMedia({ audio: true });
      await conversation.startSession({
        agentId: AGENT_ID,
        connectionType: 'websocket',
        dynamicVariables: { user_id: user.id },
      });
    } catch (err: any) {
      if (err?.name === 'NotAllowedError') {
        setError(t('voice.micDenied'));
      } else {
        setError(err?.message || t('voice.startError'));
      }
    }
  }, [conversation, user, t]);

  const handleStop = useCallback(async () => {
    try { await conversation.endSession(); } catch {}
  }, [conversation]);

  // Cleanup on unmount
  const connRef = useRef(conversation);
  connRef.current = conversation;
  useEffect(() => {
    return () => { try { connRef.current.endSession(); } catch {} };
  }, []);

  return (
    <div className="flex flex-col items-center text-center pt-4">
      <div className="relative mb-6">
        {isConnected && isSpeaking && (
          <div className="absolute -inset-3 rounded-full bg-primary-400/20 animate-pulse" />
        )}
        {isConnected && !isSpeaking && (
          <div className="absolute -inset-2 rounded-full bg-green-400/30 animate-ping" />
        )}
        <div className={`relative w-20 h-20 rounded-full flex items-center justify-center transition-colors ${
          isConnected ? (isSpeaking ? 'bg-primary-500' : 'bg-green-500') : 'bg-primary-50'
        }`}>
          <MicrophoneIcon className={`h-10 w-10 ${isConnected ? 'text-white' : 'text-primary-500'}`} />
        </div>
      </div>

      {!isConnected && !isConnecting && (
        <>
          <h2 className="text-xl font-semibold text-text-primary mb-2">{t('voice.aiAgent')}</h2>
          <p className="text-sm text-text-secondary mb-6">
            {t('voice.agentDescription')}
          </p>
        </>
      )}

      {isConnecting && <p className="text-sm text-text-secondary mb-6">{t('voice.connecting')}</p>}
      {isConnected && isSpeaking && <p className="text-sm font-medium text-primary-600 mb-6">{t('voice.assistantSpeaking')}</p>}
      {isConnected && !isSpeaking && <p className="text-sm font-medium text-green-600 mb-6">{t('voice.imListening')}</p>}

      {error && (
        <div className="w-full mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>
      )}

      {!isConnected && !isConnecting && (
        <Button size="lg" onClick={handleStart} className="w-full">{t('voice.startAgent')}</Button>
      )}
      {isConnecting && (
        <Button size="lg" disabled className="w-full">{t('voice.connectingShort')}</Button>
      )}
      {isConnected && (
        <Button variant="outline" size="lg" onClick={handleStop} className="w-full">{t('voice.endConversation')}</Button>
      )}
    </div>
  );
}
